import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { FormattedMessage } from 'react-intl';
import {
    getDebouncedServiceOrderSearch,
    getSelectedServiceOption,
    getServiceOptionSort,
    getServiceOptionStatusFilter,
    getServiceOrderModalFilters,
    serviceOrdersSlice,
} from '../../reducers/serviceOrdersSlice';
import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import { ServiceOptionsTable } from './table/ServiceOptionsTable';
import { LoadMoreButton } from '../../sharedComponents/table/LoadMoreButton';
import ErrorState from '@rio-cloud/rio-uikit/ErrorState';
import { ServiceOptionCardsSorting } from './table/ServiceOptionCardsSorting';
import { useGetServiceOptionsQuery } from '../../api/serviceOptionsApi';
import { useSetUserTriggeredFetching } from '../../sharedComponents/userTriggeredFetchingHook';
import { isUserSessionExpired } from '../../../../configuration/login/loginSlice';
import { ServiceOptionStatusFilter } from './table/toolbar/ServiceOptionStatusFilter';
import { ServiceOrderTableToolbar } from '../shared/toolbar/ServiceOrderTableToolbar';
import { TableColumnSettingsState } from '../../sharedComponents/table/TableColumnSettingsState';
import { serviceOptionColumnDefinition } from './table/ServiceOptionsTableColumns';

export const DEFAULT_SERVICE_OPTIONS_LIMIT_SIZE = 100;
export const MAX_SERVICE_OPTIONS_LIMIT_SIZE = 1000;

export const ServiceOptions = () => {
    const isSessionExpired = useAppSelector(isUserSessionExpired);
    const [limit, setLimit] = useState(DEFAULT_SERVICE_OPTIONS_LIMIT_SIZE);
    const nextLimit = limit + DEFAULT_SERVICE_OPTIONS_LIMIT_SIZE;
    const sort = useAppSelector(getServiceOptionSort);
    const modalFilters = useAppSelector(getServiceOrderModalFilters);
    const debouncedSearchTerm = useAppSelector(getDebouncedServiceOrderSearch).trim();
    const statusFilter = useAppSelector(getServiceOptionStatusFilter);

    const {
        data: serviceOptionsPage,
        isFetching,
        isLoading,
        isError,
    } = useGetServiceOptionsQuery(
        {
            limit,
            sortBy: sort,
            serviceTypes: modalFilters.serviceTypeFilter,
            orderIssuedAtAfter: modalFilters.startDateFilter,
            orderIssuedAtBefore: modalFilters.endDateFilter,
            location: modalFilters.locationFilter,
            searchTerm: debouncedSearchTerm.length === 0 ? undefined : debouncedSearchTerm,
            statuses: statusFilter,
        },
        {
            pollingInterval: isSessionExpired ? undefined : 60000,
        },
    );

    useSetUserTriggeredFetching({
        isFetching,
        depsWhichTriggerUserFetching: [sort, modalFilters, statusFilter, debouncedSearchTerm],
    });

    const selectedServiceOption = useAppSelector(getSelectedServiceOption);
    const dispatch = useAppDispatch();

    if (isLoading) {
        return (
            <div data-testid="spinner">
                <Spinner />
            </div>
        );
    }

    if (isError || typeof serviceOptionsPage === 'undefined') {
        return (
            <ErrorState
                headline={<FormattedMessage id="intl-msg:common-message.error.generic.headline" />}
                message={<FormattedMessage id="intl-msg:common-message.error.generic.message" />}
            />
        );
    }

    const serviceOptions = serviceOptionsPage.items;
    if (selectedServiceOption !== undefined) {
        if (!serviceOptions?.find((it) => it.id === selectedServiceOption.id)) {
            dispatch(serviceOrdersSlice.actions.deselectServiceOption());
        }
    }

    return (
        <TableColumnSettingsState tableId="serviceOrder.options" columnDefinition={serviceOptionColumnDefinition}>
            <ServiceOrderTableToolbar statusFilter={<ServiceOptionStatusFilter />} />
            <ServiceOptionCardsSorting />
            <ServiceOptionsTable serviceOptions={serviceOptions} />
            {serviceOptions.length > 0 && (
                <LoadMoreButton
                    onLoadMore={() => setLimit(nextLimit)}
                    hasMoreToLoad={serviceOptionsPage.hasMore}
                    limitReached={nextLimit > MAX_SERVICE_OPTIONS_LIMIT_SIZE}
                />
            )}
        </TableColumnSettingsState>
    );
};
